import { useMemo } from 'react'

import { Duration } from '@/config/model-params'
import type {
  ModelConfigItem,
  OptionWithCredits,
  ResolutionDurationRule
} from '@/lib/api/models/config'

/**
 * 根据选中的分辨率获取可用的时长列表
 * @param selectedResolution - 当前选中的分辨率值
 * @param rules - 分辨率-时长联动规则
 * @returns 支持的时长值数组，如果无限制则返回 null
 */
export const getAvailableDurationsForResolution = (
  selectedResolution: string,
  rules?: ResolutionDurationRule[]
): string[] | null => {
  if (!rules || rules.length === 0) return null // 无限制

  const rule = rules.find(r => r.resolution === selectedResolution)
  return rule?.supportedDurations ?? []
}

/**
 * 根据选中的时长获取可用的分辨率列表（反向查找）
 * @param selectedDuration - 当前选中的时长值
 * @param rules - 分辨率-时长联动规则
 * @returns 支持的分辨率值数组，如果无限制则返回 null
 */
export const getAvailableResolutionsForDuration = (
  selectedDuration: string,
  rules?: ResolutionDurationRule[]
): string[] | null => {
  if (!rules || rules.length === 0) return null // 无限制

  return rules.filter(r => r.supportedDurations.includes(selectedDuration)).map(r => r.resolution)
}

/**
 * 检查分辨率-时长组合是否有效
 * @param resolution - 要检查的分辨率值
 * @param duration - 要检查的时长值
 * @param rules - 分辨率-时长联动规则
 * @returns 如果组合有效或无规则限制则返回 true
 */
export const isValidCombination = (
  resolution: string,
  duration: string,
  rules?: ResolutionDurationRule[]
): boolean => {
  if (!rules || rules.length === 0) return true // 无限制

  const rule = rules.find(r => r.resolution === resolution)
  if (!rule) return true // 该分辨率不在规则中，允许所有时长

  return rule.supportedDurations.includes(duration)
}

/**
 * 当前选择无效时，获取该分辨率的默认时长
 * @param resolution - 分辨率值
 * @param rules - 分辨率-时长联动规则
 * @param allDurations - 所有可用的时长选项
 * @returns 有效的时长值或 undefined
 */
export const getDefaultDurationForResolution = (
  resolution: string,
  rules?: ResolutionDurationRule[],
  allDurations?: OptionWithCredits<string, Duration>[]
): Duration | undefined => {
  if (!rules || rules.length === 0 || !allDurations) {
    // 无限制，返回第一个默认选项或第一个选项
    return allDurations?.find(d => d.isDefault)?.value ?? allDurations?.[0]?.value
  }

  const availableDurations = getAvailableDurationsForResolution(resolution, rules)
  if (!availableDurations || availableDurations.length === 0) {
    return allDurations?.find(d => d.isDefault)?.value ?? allDurations?.[0]?.value
  }

  // 在可用时长中查找默认选项
  const defaultDuration = allDurations.find(
    d => d.isDefault && availableDurations.includes(d.value)
  )
  if (defaultDuration) return defaultDuration.value

  // 返回第一个可用的时长
  const firstAvailable = allDurations.find(d => availableDurations.includes(d.value))
  return firstAvailable?.value
}

/**
 * 当前选择无效时，获取该时长的默认分辨率
 * @param duration - 时长值
 * @param rules - 分辨率-时长联动规则
 * @param allResolutions - 所有可用的分辨率选项
 * @returns 有效的分辨率值或 undefined
 */
export const getDefaultResolutionForDuration = (
  duration: string,
  rules?: ResolutionDurationRule[],
  allResolutions?: OptionWithCredits<string, string>[]
): string | undefined => {
  if (!rules || rules.length === 0 || !allResolutions) {
    // 无限制，返回第一个默认选项或第一个选项
    return allResolutions?.find(r => r.isDefault)?.value ?? allResolutions?.[0]?.value
  }

  const availableResolutions = getAvailableResolutionsForDuration(duration, rules)
  if (!availableResolutions || availableResolutions.length === 0) {
    return allResolutions?.find(r => r.isDefault)?.value ?? allResolutions?.[0]?.value
  }

  // 在可用分辨率中查找默认选项
  const defaultResolution = allResolutions.find(
    r => r.isDefault && availableResolutions.includes(r.value)
  )
  if (defaultResolution) return defaultResolution.value

  // 返回第一个可用的分辨率
  const firstAvailable = allResolutions.find(r => availableResolutions.includes(r.value))
  return firstAvailable?.value
}

/**
 * 根据选中的分辨率过滤时长选项
 * @param durations - 所有时长选项
 * @param selectedResolution - 当前选中的分辨率
 * @param rules - 分辨率-时长联动规则
 * @returns 过滤后的时长选项
 */
export const filterDurationsByResolution = <T extends OptionWithCredits<string, Duration>>(
  durations: T[],
  selectedResolution: string,
  rules?: ResolutionDurationRule[]
): T[] => {
  const availableDurations = getAvailableDurationsForResolution(selectedResolution, rules)
  if (!availableDurations) return durations // 无限制

  return durations.filter(d => availableDurations.includes(d.value))
}

/**
 * 根据选中的时长过滤分辨率选项
 * @param resolutions - 所有分辨率选项
 * @param selectedDuration - 当前选中的时长
 * @param rules - 分辨率-时长联动规则
 * @returns 过滤后的分辨率选项
 */
export const filterResolutionsByDuration = <T extends OptionWithCredits<string, string>>(
  resolutions: T[],
  selectedDuration: string,
  rules?: ResolutionDurationRule[]
): T[] => {
  const availableResolutions = getAvailableResolutionsForDuration(selectedDuration, rules)
  if (!availableResolutions) return resolutions // 无限制

  return resolutions.filter(r => availableResolutions.includes(r.value))
}

/**
 * 根据当前选择获取联动后的时长和分辨率选项的 Hook
 */
export const useLinkedOptions = (
  model: ModelConfigItem | undefined,
  selectedResolution: string,
  selectedDuration: Duration
) => {
  const rules = model?.resolutionDurationRules
  const durationOptions = model?.durationOptions
  const resolutionOptions = model?.resolutionOptions

  const filteredDurations = useMemo(() => {
    const allDurations = durationOptions ?? []
    return filterDurationsByResolution(allDurations, selectedResolution, rules)
  }, [durationOptions, selectedResolution, rules])

  const filteredResolutions = useMemo(() => {
    const allResolutions = resolutionOptions ?? []
    return filterResolutionsByDuration(allResolutions, selectedDuration, rules)
  }, [resolutionOptions, selectedDuration, rules])

  const isCurrentCombinationValid = useMemo(
    () => isValidCombination(selectedResolution, selectedDuration, rules),
    [selectedResolution, selectedDuration, rules]
  )

  return {
    filteredDurations,
    filteredResolutions,
    isCurrentCombinationValid,
    hasLinkingRules: !!rules && rules.length > 0
  }
}
